import { createSlice, createAsyncThunk, type PayloadAction } from "@reduxjs/toolkit"
import type { User } from "../../contexts/auth-context"
import apiClient from "../../api/apiClient"

interface UsersState {
  users: User[]
  isLoading: boolean
  error: string | null
}

const initialState: UsersState = {
  users: [],
  isLoading: false,
  error: null,
}

// Helper function to extract error message from API response
const extractErrorMessage = (error: any): string => {
  if (error.response?.data?.error) {
    return error.response.data.error
  } else if (error.message) {
    return error.message
  } else {
    return "An unknown error occurred"
  }
}

// Async thunks
export const fetchUsers = createAsyncThunk(
  "users/fetchUsers",
  async (params: { status?: string } | undefined, { rejectWithValue }) => {
    try {
      const status = params?.status
      const response = await apiClient.get(`/users${status && status !== "all" ? `?status=${status}` : ""}`)
      return response.data.users
    } catch (error: any) {
      return rejectWithValue(extractErrorMessage(error))
    }
  },
)

export const approveUser = createAsyncThunk("users/approveUser", async (userId: string, { rejectWithValue }) => {
  try {
    const response = await apiClient.put(`/users/${userId}/approve`)
    return { success: true, userId }
  } catch (error: any) {
    return rejectWithValue(extractErrorMessage(error))
  }
})

export const updateUserRole = createAsyncThunk(
  "users/updateUserRole",
  async ({ userId, role }: { userId: string; role: string }, { rejectWithValue }) => {
    try {
      const response = await apiClient.put(`/users/${userId}/role`, { role })
      return { success: true, userId, role }
    } catch (error: any) {
      return rejectWithValue(extractErrorMessage(error))
    }
  },
)

export const deleteUser = createAsyncThunk("users/deleteUser", async (userId: string, { rejectWithValue }) => {
  try {
    const response = await apiClient.delete(`/users/${userId}`)
    return { success: true, userId, message: response.data.message }
  } catch (error: any) {
    console.error("Delete user error:", error)
    return rejectWithValue(extractErrorMessage(error))
  }
})

export const updateUserProfile = createAsyncThunk(
  "users/updateUserProfile",
  async (
    {
      userId,
      fullName,
      phoneNumber,
      building,
      roomNumber,
      profilePhoto,
      canReceiveMessages,
      muteGuestNotifications,
      disableEmailNotifications,
      managedBuildings,
      managedTicketCategories,
    }: {
      userId?: string
      fullName: string
      phoneNumber: string
      building: string
      roomNumber: string
      profilePhoto?: File
      canReceiveMessages?: boolean
      muteGuestNotifications?: boolean
      disableEmailNotifications?: boolean
      managedBuildings?: string[]
      managedTicketCategories?: string[]
    },
    { rejectWithValue },
  ) => {
    try {
      // Validate required fields
      if (!fullName.trim()) {
        return rejectWithValue("Full name is required")
      }

      if (!building || building === "undefined" || building.trim() === "") {
        return rejectWithValue("Building is required")
      }

      if (!roomNumber.trim()) {
        return rejectWithValue("Room number is required")
      }

      // If userId is provided, use it for admin updates, otherwise use the current user's profile
      const endpoint = userId ? `/users/${userId}` : "/users/profile"

      // Create FormData if there's a profile photo
      let data
      let headers = {}

      if (profilePhoto) {
        data = new FormData()
        data.append("fullName", fullName)
        data.append("phoneNumber", phoneNumber)
        data.append("building", building)
        data.append("roomNumber", roomNumber)
        data.append("profilePhoto", profilePhoto)
        if (canReceiveMessages !== undefined) {
          data.append("canReceiveMessages", canReceiveMessages.toString())
        }
        if (muteGuestNotifications !== undefined) {
          data.append("muteGuestNotifications", muteGuestNotifications.toString())
        }
        if (disableEmailNotifications !== undefined) {
          data.append("disableEmailNotifications", disableEmailNotifications.toString())
        }
        if (managedBuildings !== undefined) {
          // FormData can't handle arrays directly, so we send it as JSON
          data.append("managedBuildings", JSON.stringify(managedBuildings))
        }
        if (managedTicketCategories !== undefined) {
          data.append("managedTicketCategories", JSON.stringify(managedTicketCategories))
        }
        headers = { "Content-Type": "multipart/form-data" }
      } else {
        data = {
          fullName,
          phoneNumber,
          building,
          roomNumber,
        } as any
        if (canReceiveMessages !== undefined) {
          data.canReceiveMessages = canReceiveMessages
        }
        if (muteGuestNotifications !== undefined) {
          data.muteGuestNotifications = muteGuestNotifications
        }
        if (disableEmailNotifications !== undefined) {
          data.disableEmailNotifications = disableEmailNotifications
        }
        if (managedBuildings !== undefined) {
          data.managedBuildings = managedBuildings
        }
        if (managedTicketCategories !== undefined) {
          data.managedTicketCategories = managedTicketCategories
        }
        headers = { "Content-Type": "application/json" }
      }

      const response = await apiClient.put(endpoint, data, { headers })
      return { success: true, profile: response.data.profile }
    } catch (error: any) {
      return rejectWithValue(extractErrorMessage(error))
    }
  },
)

export const updateProfilePhoto = createAsyncThunk(
  "users/updateProfilePhoto",
  async (photo: File, { rejectWithValue }) => {
    try {
      const formData = new FormData()
      formData.append("photo", photo)

      const response = await apiClient.put("/users/profile/photo", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })

      return { success: true, photoUrl: response.data.photoUrl }
    } catch (error: any) {
      return rejectWithValue(extractErrorMessage(error))
    }
  },
)

const usersSlice = createSlice({
  name: "users",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch users
      .addCase(fetchUsers.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchUsers.fulfilled, (state, action: PayloadAction<User[]>) => {
        state.users = action.payload
        state.isLoading = false
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      // Approve user
      .addCase(approveUser.fulfilled, (state, action) => {
        const user = state.users.find((user) => user.id === action.payload.userId)
        if (user) {
          user.isApproved = true
        }
      })
      // Update user role
      .addCase(updateUserRole.fulfilled, (state, action) => {
        const user = state.users.find((user) => user.id === action.payload.userId)
        if (user) {
          user.role = action.payload.role as "user" | "admin"
        }
      })
      // Delete user
      .addCase(deleteUser.fulfilled, (state, action) => {
        if (action.payload.success) {
          state.users = state.users.filter((user) => user.id !== action.payload.userId)
        }
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.error = action.payload as string
      })
      // Update user profile
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        if (action.payload.success && action.payload.profile) {
          const userIndex = state.users.findIndex((user) => user.id === action.payload.profile.id)
          if (userIndex !== -1) {
            // Update the user with the new profile data
            state.users[userIndex] = {
              ...state.users[userIndex],
              ...action.payload.profile,
            }
          }
        }
      })
  },
})

export default usersSlice.reducer
